import React, { useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useEntityList } from '../data/entities';
import { useToast } from '../components/Toast';
import { recommendLesson, lessonStats, syncSamplePack, styleLabel } from './lessonCatalog';
import { useUserProfile } from './useUserProfile';
import { planLabel } from './planTypes';
import { resolvePoseRef } from './poseImages';
import {
  continueTarget,
  formatDuration,
  formatRelativeDay,
  practicePath,
  readPracticeHistory,
} from './practiceSession';

function greetingForNow() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function tierClass(planType) {
  if (planType === 'karma') return 'yoga-tier yoga-tier--karma';
  if (planType === 'guided') return 'yoga-tier yoga-tier--guided';
  if (planType === 'teacher') return 'yoga-tier yoga-tier--teacher';
  return 'yoga-tier yoga-tier--guided';
}

export default function Today() {
  const toast = useToast();
  const navigate = useNavigate();
  const { uid, profile, planType } = useUserProfile();
  const { docs: lessons } = useEntityList('yogaLesson', {});
  const autoSyncRan = useRef(false);

  useEffect(() => {
    if (!uid || !planType || lessons == null) return;
    if (autoSyncRan.current) return;
    autoSyncRan.current = true;
    (async () => {
      try {
        await syncSamplePack(lessons, { force: false });
      } catch (err) {
        toast(`Could not refresh classes: ${err.message}`, 'danger', 6000);
      }
    })();
  }, [uid, planType, lessons, toast]);

  const recommended = useMemo(
    () => recommendLesson(lessons, profile, planType),
    [lessons, profile, planType],
  );

  const stats = recommended ? lessonStats(recommended, planType) : null;
  const thumb = stats?.sequence?.[0]
    ? resolvePoseRef(stats.sequence[0].poseKey)
    : null;

  const cont = useMemo(
    () => continueTarget(profile, { recommendedId: recommended?.id }),
    [profile, recommended?.id],
  );

  const history = useMemo(() => readPracticeHistory(profile), [profile]);

  const continueLesson = useMemo(() => {
    if (!cont?.lessonId || !lessons) return null;
    return lessons.find((l) => l.id === cont.lessonId) || null;
  }, [cont, lessons]);

  const continueThumb = useMemo(() => {
    if (!continueLesson || !planType) return null;
    const seq = lessonStats(continueLesson, planType)?.sequence?.[0];
    return seq ? resolvePoseRef(seq.poseKey) : null;
  }, [continueLesson, planType]);

  const name = profile?.displayName?.trim();
  const streak = Number(profile?.streakDays) || 0;
  const sessions = Number(profile?.totalSessions) || 0;
  const lastAt = profile?.lastPracticedAt
    ? new Date(profile.lastPracticedAt).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    })
    : null;
  const initial = (name || 'Y').slice(0, 1).toUpperCase();

  return (
    <div className="cs-page yoga-shell u-stack m-fade-in" style={{ '--gap': 'var(--sp-6)' }}>
      <header className="yoga-page-head m-rise">
        <h1 className="yoga-hero-brand">YogaKai</h1>
        <p className="yoga-eyebrow" style={{ marginTop: 4 }}>
          {greetingForNow()}
          {name ? `, ${name}` : ''}
        </p>
      </header>

      <div className="yoga-today-hero m-rise" style={{ '--m-delay': '40ms' }}>
        {recommended ? (
          <section className="yoga-feature">
            <p className="yoga-eyebrow">Recommended for you</p>
            <div className="yoga-feature__row">
              <div className="u-stack" style={{ '--gap': '8px', flex: 1, minWidth: 0 }}>
                <h2>{recommended.title}</h2>
                <p className="yoga-feature__meta">
                  {styleLabel(recommended.style)} · {recommended.skillLevel} · {stats.durationMins} min · {stats.poseCount} poses
                </p>
                {recommended.description ? (
                  <p>{recommended.description}</p>
                ) : (
                  <p>A timed class matched to your plan, level, and preferred minutes.</p>
                )}
                <div className="u-cluster" style={{ '--gap': '8px', marginTop: 8 }}>
                  <button
                    type="button"
                    className="cs-btn"
                    onClick={() => navigate(`/practice/${recommended.id}`)}
                  >
                    Start practice
                  </button>
                  <Link className="cs-btn cs-btn-ghost" to="/library">
                    Browse library
                  </Link>
                </div>
              </div>
              {thumb?.url ? (
                <img src={thumb.url} alt="" className="yoga-feature__img" />
              ) : (
                <div className="yoga-feature__img yoga-feature__img--empty" aria-hidden />
              )}
            </div>
          </section>
        ) : (
          <section className="yoga-feature">
            <p className="yoga-eyebrow">Today</p>
            <h2>Getting your class ready</h2>
            <p>Classes are loading — or open the Library once your pack is ready.</p>
            <Link className="cs-btn" to="/library" style={{ alignSelf: 'flex-start' }}>
              Open library
            </Link>
          </section>
        )}

        <aside className="yoga-panel">
          <div className="u-cluster" style={{ '--gap': '12px', alignItems: 'center' }}>
            <div className="yoga-avatar" aria-hidden>{initial}</div>
            <div className="u-stack" style={{ '--gap': '4px', minWidth: 0 }}>
              <strong style={{ fontWeight: 600 }}>{name || 'Your practice'}</strong>
              {planType ? (
                <span className={tierClass(planType)}>{planLabel(planType)}</span>
              ) : null}
            </div>
          </div>
          <div className="yoga-today-stats">
            <div className="yoga-today-stat-cell">
              Streak
              <span className="yoga-today-stat">
                {streak}
                <small style={{ fontSize: '0.7rem', marginLeft: 4 }}>day{streak === 1 ? '' : 's'}</small>
              </span>
            </div>
            <div className="yoga-today-stat-cell">
              Sessions
              <span className="yoga-today-stat">{sessions}</span>
            </div>
            <div className="yoga-today-stat-cell">
              Last
              <span className="yoga-today-stat" style={{ fontSize: '1.05rem' }}>
                {lastAt || '—'}
              </span>
            </div>
          </div>
          <p className="u-muted u-small" style={{ margin: 0 }}>
            <Link to="/profile">Edit preferences</Link>
            {' · '}
            <Link to="/membership">Membership</Link>
          </p>
        </aside>
      </div>

      {cont ? (
        <section className="yoga-continue m-rise" style={{ '--m-delay': '80ms' }}>
          <div className="yoga-continue__copy">
            <p className="yoga-eyebrow">
              {cont.kind === 'resume' ? 'Pick up where you left off' : 'Continue last class'}
            </p>
            <h2>{cont.title}</h2>
            <p className="u-muted" style={{ margin: 0 }}>
              {cont.kind === 'resume'
                ? `Resume from pose ${cont.stepIndex + 1}${continueLesson ? ` · ${styleLabel(continueLesson.style)}` : ''}`
                : `${formatRelativeDay(cont.completedAt) || 'Recently'}${continueLesson ? ` · ${styleLabel(continueLesson.style)} · ${continueLesson.skillLevel}` : ''}`}
            </p>
            <button
              type="button"
              className="cs-btn"
              style={{ alignSelf: 'flex-start', marginTop: 4 }}
              onClick={() => navigate(practicePath(cont))}
            >
              {cont.kind === 'resume' ? 'Continue practice' : 'Practice again'}
            </button>
          </div>
          {continueThumb?.url ? (
            <img src={continueThumb.url} alt="" className="yoga-continue__img" />
          ) : (
            <div className="yoga-continue__img yoga-continue__img--empty" aria-hidden />
          )}
        </section>
      ) : null}

      <section className="yoga-history m-rise" style={{ '--m-delay': '120ms' }}>
        <div className="yoga-history__head">
          <p className="yoga-eyebrow" style={{ margin: 0 }}>Recent practice</p>
          {history.length ? (
            <span className="u-muted u-small">{history.length} session{history.length === 1 ? '' : 's'}</span>
          ) : null}
        </div>
        {history.length ? (
          <ul className="yoga-history__list">
            {history.map((entry) => (
              <li key={`${entry.lessonId}-${entry.completedAt}`}>
                <button
                  type="button"
                  className="yoga-history__row"
                  onClick={() => navigate(`/practice/${entry.lessonId}`)}
                >
                  <span className="yoga-history__title">{entry.title}</span>
                  <span className="yoga-history__meta">
                    {formatRelativeDay(entry.completedAt)}
                    {entry.practicedSeconds
                      ? ` · ${formatDuration(entry.practicedSeconds)}`
                      : ''}
                    {entry.style ? ` · ${styleLabel(entry.style)}` : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="yoga-history__empty">
            Finish a class and it will show up here — then you can start it again in one tap.
          </p>
        )}
      </section>
    </div>
  );
}
